import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import {
  fetchSinglePlan,
  fetchActiveTasks,
  fetchAlerts,
  fetchHorizonResources,
  fetchResourceStatus,
} from './operationsSlice'
import { PLAN_STATUS_COLORS, HORIZON_STATE_COLORS } from './types/operations.types'
import { OperationsTabBar } from './components/OperationsTabBar'
import { TaskRow } from './components/TaskRow'
import { AlertCard } from './components/AlertCard'
import { ResourceStatusPanel } from './components/ResourceStatusPanel'
import { QueueHealthBadge } from './components/QueueHealthBadge'
import {
  OpsPageWrapper,
  OpsCard,
  OpsCardHeader,
  OpsGrid,
  OpsTable,
  OpsTableHead,
  OpsButton,
  Badge,
  ProgressBarWrapper,
  ProgressBarFill,
  opsColors,
  HorizonBlock,
} from './styled/Operations.styled'
import { NavBar } from 'components/NavBar/NavBar'

export const OperationPlanControlRoom: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const dispatch = useDispatch<AppDispatch>()
  const { singlePlan, activeTasks, alerts, horizonResources, resourceStatus } = useSelector(
    (state: RootState) => state.operations,
  )

  useEffect(() => {
    if (id) {
      dispatch(fetchSinglePlan(id))
      dispatch(fetchActiveTasks(id))
      dispatch(fetchAlerts(id))
      dispatch(fetchHorizonResources())
      dispatch(fetchResourceStatus())
    }
  }, [dispatch, id])

  const plan = singlePlan

  return (
    <>
      <NavBar />
      <OpsPageWrapper>
        <h1 style={{ fontSize: '30px', fontWeight: 900, color: opsColors.textPrimary, margin: 0, fontFamily: 'DM Sans, sans-serif' }}>
          Control Room
        </h1>

        <OperationsTabBar />

        {/* Plan Overview Strip */}
        {plan && (
          <OpsCard>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <h2 style={{ fontSize: '24px', fontWeight: 400, color: opsColors.textPrimary, margin: 0 }}>{plan.name}</h2>
                <Badge bg={PLAN_STATUS_COLORS[plan.status]}>{plan.status}</Badge>
                <QueueHealthBadge health={plan.queueHealth} />
                <Badge bg={opsColors.brand500}>{plan.priority}</Badge>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {plan.status === 'Active' && <OpsButton variant="secondary" size="sm">Pause</OpsButton>}
                {plan.status === 'Paused' && <OpsButton variant="success" size="sm">Resume</OpsButton>}
                <OpsButton variant="secondary" size="sm">Configure</OpsButton>
              </div>
            </div>

            <ProgressBarWrapper height="6px" style={{ marginBottom: '0.75rem' }}>
              <ProgressBarFill percent={plan.progress} />
            </ProgressBarWrapper>

            <div style={{ display: 'flex', gap: '2rem', fontSize: '14px' }}>
              <div>
                <span style={{ color: opsColors.textFaded }}>Target: </span>
                <span style={{ color: opsColors.textPrimary, fontWeight: 700 }}>{plan.targetValue} {plan.targetUnit}</span>
              </div>
              <div>
                <span style={{ color: opsColors.textFaded }}>Current: </span>
                <span style={{ color: opsColors.textPrimary, fontWeight: 700 }}>{plan.currentValue} {plan.targetUnit}</span>
              </div>
              <div>
                <span style={{ color: opsColors.textFaded }}>Progress: </span>
                <span style={{ color: opsColors.textPrimary, fontWeight: 700 }}>{plan.progress}%</span>
              </div>
              {plan.estCompletion && (
                <div>
                  <span style={{ color: opsColors.textFaded }}>Est. Completion: </span>
                  <span style={{ color: opsColors.textPrimary, fontWeight: 700 }}>{new Date(plan.estCompletion).toLocaleDateString()}</span>
                </div>
              )}
              <div>
                <span style={{ color: opsColors.textFaded }}>Workers: </span>
                <span style={{ color: opsColors.textPrimary, fontWeight: 700 }}>{plan.workers}</span>
              </div>
              <div>
                <span style={{ color: opsColors.textFaded }}>Machines: </span>
                <span style={{ color: opsColors.textPrimary, fontWeight: 700 }}>{plan.machines}</span>
              </div>
              <div>
                <span style={{ color: opsColors.textFaded }}>Cycles: </span>
                <span style={{ color: opsColors.textPrimary, fontWeight: 700 }}>{plan.activeCycles}</span>
              </div>
            </div>
          </OpsCard>
        )}

        {/* Main panels */}
        <OpsGrid columns="65fr 35fr" gap="1.25rem">
          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Active Operations */}
            <OpsCard noPadding>
              <div style={{ padding: '1.25rem 1.25rem 0.5rem' }}>
                <OpsCardHeader>
                  <h3>Active Operations</h3>
                  <span style={{ fontSize: '12px', color: opsColors.textFaded }}>{activeTasks?.length || 0} tasks</span>
                </OpsCardHeader>
              </div>
              <OpsTable>
                <OpsTableHead>
                  <tr>
                    <th>Task/Cycle</th>
                    <th>Machine</th>
                    <th>Operator</th>
                    <th>Status</th>
                    <th>ETA</th>
                    <th>Location</th>
                    <th>Source</th>
                  </tr>
                </OpsTableHead>
                <tbody>
                  {activeTasks?.map((task) => (
                    <TaskRow key={task.id} task={task} />
                  ))}
                </tbody>
              </OpsTable>
            </OpsCard>

            {/* Rolling Horizon Queue Preview */}
            <OpsCard>
              <OpsCardHeader>
                <h3>Rolling Horizon Queue</h3>
                <span style={{ fontSize: '12px', color: opsColors.brand500, cursor: 'pointer' }}>View Full Horizon &rarr;</span>
              </OpsCardHeader>
              <OpsGrid columns="1fr 1fr" gap="0.75rem">
                {horizonResources?.slice(0, 4).map((resource) => (
                  <div key={resource.id} style={{ background: opsColors.bgPrimary, borderRadius: '8px', padding: '0.75rem' }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: opsColors.textSecondary, marginBottom: '0.5rem' }}>
                      {resource.name}
                    </div>
                    <div style={{ display: 'flex', gap: '2px', height: '24px' }}>
                      {resource.tasks.map((task) => {
                        const colors = HORIZON_STATE_COLORS[task.state]
                        return (
                          <HorizonBlock
                            key={task.id}
                            color={colors.bg}
                            textColor={colors.text}
                            flex={task.durationMinutes}
                            blocked={task.state === 'blocked'}
                          >
                            {task.name}
                          </HorizonBlock>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </OpsGrid>
            </OpsCard>
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Operational Risks */}
            <OpsCard>
              <OpsCardHeader>
                <h3>Operational Risks</h3>
              </OpsCardHeader>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                {alerts?.slice(0, 3).map((alert) => (
                  <AlertCard key={alert.id} alert={alert} />
                ))}
              </div>
            </OpsCard>

            {/* Resource Status */}
            {resourceStatus && <ResourceStatusPanel data={resourceStatus} />}
          </div>
        </OpsGrid>
      </OpsPageWrapper>
    </>
  )
}
